import React, { useMemo, useRef } from 'react'
import { Divider } from '@retailer-platform/shared-components/src/tds'
import { LoadingSpinner, LegacySwitchBase as SwitchBase } from '@instacart/ids-customers'
import styled from '@emotion/styled'
import { Alert, Text, spacing } from '@retailer-platform/shared-components'
import faker from 'faker'
import {
  AttributeManagementCategoryEnum,
  type PipelineStep,
} from '../../../types/attributeManagement.types'
import {
  PropertyContainer,
  PropertyRow,
  SectionHeader,
} from '../../shared-styled-components/SharedStyledComponents'
import { useDomainMessages } from '../../../utils/domain/intl'
import { Instacart_Catalog_Catflow_V1_StepCategory, useListStepsQuery } from '../../../api'
import { useDomainAccessControl } from '../../../utils/domain/accessControl.ts'
import { showCampPipelineEditor } from '../../../access-control/catalogAdminExtractionModelsAccess.configuration.ts'
import { validateCronExpression } from './schedule-components/cronUtils'
import PipelineFormStepTile, { EndTile, JoinChevronButton } from './PipelineFormStepTile'
import { type FormProps } from './StepAttributeForm'
import { ScheduleCard } from './ScheduleCard'
import { PipelineEditor } from './pipeline-editor/PipelineEditor.tsx'
import { type PipelineEditorAttributes } from './pipeline-editor/types.ts'

const TileContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.X16,
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  flex: 1,
})

const StyledCronContainer = styled.div({
  paddingLeft: spacing.X16,
})

const PipelineForm: React.FC<FormProps> = ({ attributes, mode, onChange }) => {
  const i18n = useDomainMessages({
    scheduleTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.scheduleTitle',
    stepsTitle: 'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepsTitle',
    enableSchedule:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.enableSchedule',
    hoursInterval:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.hoursInterval',
    unscheduled:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.unscheduled',
    scheduleUtcNotice:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.scheduleUtcNotice',
  })

  const idempotencyKey = useRef(faker.random.uuid())
  const hasAccess = useDomainAccessControl()
  const hasAccessToPipelineEditor = hasAccess(showCampPipelineEditor)

  // Not my favourite code, but for now it doesn't make sense for inputs to be anywhere but at the front. so i need to know all the input step ids to properly render the UI.
  const [inputStepIds, setInputStepIds] = React.useState<string[]>([])
  const [standardCron, setStandardCron] = React.useState(attributes.cronExpression || '0 9 * * 1')
  const [cronError, setCronError] = React.useState<string | null>(null)
  const { loading: stepsLoading } = useListStepsQuery({
    variables: {
      clientId: 'catalog_camp_ipp',
      stepCategory: Instacart_Catalog_Catflow_V1_StepCategory.Input,
      idempotencyKey: idempotencyKey.current,
      pageSize: '1000',
    },
    onCompleted: data => {
      const steps = data?.instacart_catalog_catflow_v1_CatflowAPI_ListSteps?.steps || []
      const stepIds: string[] = []
      for (const step of steps) {
        if (!step || !step?.id) continue
        const stepId = step.id
        stepIds.push(stepId)
      }
      setInputStepIds(stepIds)
    },
  })

  const { inputStep, otherSteps } = useMemo(() => {
    if (
      inputStepIds &&
      attributes.steps.length > 0 &&
      inputStepIds.includes(attributes.steps[0]?.stepId)
    ) {
      return { inputStep: attributes.steps[0], otherSteps: attributes.steps.slice(1) }
    }
    return { inputStep: null, otherSteps: attributes.steps }
  }, [inputStepIds, attributes.steps])

  if (stepsLoading) {
    return <LoadingSpinner />
  }

  return (
    <>
      <SectionHeader>{i18n.scheduleTitle}</SectionHeader>
      {mode === 'edit' && (
        <div style={{ paddingLeft: spacing.X16 }}>
          <Alert variant={'info'}>
            <Text weight="bold">{i18n.scheduleUtcNotice}</Text>
          </Alert>
        </div>
      )}
      <PropertyContainer>
        <PropertyRow>
          <Text weight="bold"> {i18n.enableSchedule} </Text>
          <SwitchBase
            checked={attributes.enableSchedule}
            disabled={mode !== 'edit'}
            description={i18n.enableSchedule}
            onChange={event => {
              const isEnabled = event.target.checked
              onChange({
                ...attributes,
                enableSchedule: isEnabled,
                cronExpression: isEnabled ? standardCron : undefined,
              })
            }}
          />
        </PropertyRow>
      </PropertyContainer>
      <StyledCronContainer>
        {cronError && attributes.enableSchedule && (
          <div style={{ marginBottom: spacing.X16 }}>
            <Alert variant={'danger'}>
              <Text weight={'bold'}>{cronError}</Text>
            </Alert>
          </div>
        )}
        <ScheduleCard
          disabled={mode !== 'edit' || !attributes.enableSchedule}
          onCronExpressionChange={(expression: string) => {
            setStandardCron(expression)
            const validation = validateCronExpression(expression)
            setCronError(validation.error || null)
            onChange({
              ...attributes,
              cronExpression: expression,
              cronError: validation.error || null,
            })
          }}
          onScheduleTypeChange={() => {
            setCronError(null)
          }}
          defaultType={attributes.cronExpression ? 'custom' : 'hourly'}
          initialCronExpression={attributes.cronError ? undefined : attributes.cronExpression}
        />
      </StyledCronContainer>
      <Divider />
      <SectionHeader>{i18n.stepsTitle}</SectionHeader>
      <PropertyContainer>
        <TileContainer>
          {hasAccessToPipelineEditor && (
            <PipelineEditor
              disabled={mode !== 'edit'}
              attributes={attributes as PipelineEditorAttributes}
              onSave={newAttributes => {
                onChange({
                  ...attributes,
                  ...newAttributes,
                })
              }}
            />
          )}
          {!hasAccessToPipelineEditor && (
            <>
              {/* Input Step that must come first */}
              <PipelineFormStepTile
                disabled={mode !== 'edit'}
                category={AttributeManagementCategoryEnum.Input}
                step={inputStep}
                onChange={step => {
                  onChange({ ...attributes, steps: [step, ...otherSteps] })
                }}
                onDelete={() => {
                  onChange({ ...attributes, steps: otherSteps })
                }}
                headerOverride={inputStep ? null : <Text weight="bold">Input (Optional)</Text>}
              />
              {/* Other Steps that must come after the input step */}
              {otherSteps.length > 0 &&
                otherSteps.map((step: PipelineStep, index: number) => (
                  <>
                    <JoinChevronButton
                      onAdd={step => {
                        onChange({
                          ...attributes,
                          steps: inputStep
                            ? [inputStep, ...otherSteps.toSpliced(index, 0, step)]
                            : otherSteps.toSpliced(index, 0, step),
                        })
                      }}
                      disabled={mode !== 'edit'}
                    />
                    <PipelineFormStepTile
                      disabled={mode !== 'edit'}
                      step={step}
                      onChange={step => {
                        onChange({
                          ...attributes,
                          steps: inputStep
                            ? [inputStep, ...otherSteps.toSpliced(index, 1, step)]
                            : otherSteps.toSpliced(index, 1, step),
                        })
                      }}
                      onDelete={() => {
                        onChange({
                          ...attributes,
                          steps: inputStep
                            ? [inputStep, ...otherSteps.toSpliced(index, 1)]
                            : otherSteps.toSpliced(index, 1),
                        })
                      }}
                    />
                  </>
                ))}
              {mode === 'edit' && (
                <>
                  <JoinChevronButton
                    onAdd={step => {
                      onChange({ ...attributes, steps: [...attributes.steps, step] })
                    }}
                    disabled={mode !== 'edit'}
                  />
                  <EndTile
                    onAdd={step => {
                      onChange({ ...attributes, steps: [...attributes.steps, step] })
                    }}
                  />
                </>
              )}
            </>
          )}
        </TileContainer>
      </PropertyContainer>
    </>
  )
}

export default PipelineForm
